import React, { useState } from 'react';
import { useNotification } from '../NotificationProvider';
import './NewLocation.scss';
import api from '../../services/apiService';

function NewLocation({ onClose, onLocationAdded }) {
  const { showSuccess } = useNotification();
  const [formData, setFormData] = useState({
    name: '',
    address_1: '',
    address_2: '',
    town: '',
    county: '',
    postcode: '',
    description: '',
    facilities: '',
    organiser_contact: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const validateForm = () => {
    const newErrors = {};
    const requiredFields = ['name', 'address_1', 'town', 'county', 'postcode'];
    
    requiredFields.forEach(field => {
      if (!formData[field].trim()) {
        newErrors[field] = 'This field is required';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return; // Prevent duplicate submissions
    }
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await api.post(`${process.env.REACT_APP_API_URL}/add_pitch.php`, formData);
      
      if (response.data.success) {
        showSuccess('Location added successfully!');
        if (onLocationAdded) {
          onLocationAdded({
            pitch_id: response.data.pitch_id,
            ...formData
          });
        }
        onClose();
      } else {
        setErrors(prev => ({
          ...prev,
          submit: response.data.message
        }));
      }
    } catch (error) {
      console.error('Error adding location:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Error adding location. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="new-location-modal modal-overlay">
      <div className="modal-content modal">
        <button className="modal-close" onClick={onClose}>&times;</button>
        <h2>Add New Location</h2>
        {errors.submit && <div className="error-message">{errors.submit}</div>}
        <form onSubmit={handleSubmit}>
          <div className={`form-group ${errors.name ? 'has-error' : ''}`}>
            <label htmlFor="name">
              Venue Name*
              {errors.name && <span className="error-text">{errors.name}</span>}
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>

          <div className={`form-group ${errors.address_1 ? 'has-error' : ''}`}>
            <label htmlFor="address_1">
              Address Line 1*
              {errors.address_1 && <span className="error-text">{errors.address_1}</span>}
            </label>
            <input
              type="text"
              id="address_1"
              name="address_1"
              value={formData.address_1}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="address_2">Address Line 2:</label>
            <input
              type="text"
              id="address_2"
              name="address_2"
              value={formData.address_2}
              onChange={handleChange}
            />
          </div>

          <div className="form-row">
            <div className={`form-group ${errors.town ? 'has-error' : ''}`}>
              <label htmlFor="town">
                Town*
                {errors.town && <span className="error-text">{errors.town}</span>}
              </label>
              <input
                type="text"
                id="town"
                name="town"
                value={formData.town}
                onChange={handleChange}
                required
              />
            </div>

            <div className={`form-group ${errors.county ? 'has-error' : ''}`}>
              <label htmlFor="county">
                County*
                {errors.county && <span className="error-text">{errors.county}</span>}
              </label>
              <input
                type="text"
                id="county"
                name="county"
                value={formData.county}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div className={`form-group ${errors.postcode ? 'has-error' : ''}`}>
            <label htmlFor="postcode">
              Postcode*
              {errors.postcode && <span className="error-text">{errors.postcode}</span>}
            </label>
            <input
              type="text"
              id="postcode"
              name="postcode"
              value={formData.postcode}
              onChange={handleChange}
              required
            />
          </div>

          <div className="form-group">
            <label htmlFor="description">Description:</label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows="3"
            />
          </div>

          <div className="form-group">
            <label htmlFor="facilities">Facilities:</label>
            <input
              type="text"
              id="facilities"
              name="facilities"
              value={formData.facilities}
              onChange={handleChange}
              placeholder="e.g., Parking, Changing Rooms, Floodlights"
            />
          </div>

          <div className="form-group">
            <label htmlFor="organiser_contact">Contact Details:</label>
            <input
              type="text"
              id="organiser_contact"
              name="organiser_contact"
              value={formData.organiser_contact}
              onChange={handleChange}
            />
          </div>

          <div className="form-actions modal-buttons">
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding Location...' : 'Add Location'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NewLocation; 